
import React from 'react';
import { Link } from 'react-router-dom';

const ActivityGuide = () => {
  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
        使い方 - アクティビティ
      </h1>
      <p className="text-lg text-gray-800 mb-6">
        「アクティビティ」では、Billwave内で行われた重要な操作履歴を確認することができます。
      </p>
      <p className="text-gray-700 mb-4">
        誰が、いつ、どのような操作を行ったかを記録しており、運用状況の確認や設定変更履歴の確認に利用できます。
      </p>
      <p className="text-gray-700 mb-6">
        管理者は組織内のメンバーによる操作履歴を確認できるため、請求業務や顧客管理の状況把握にも役立ちます。
      </p>

      <hr className="my-8 border-gray-200" />

      {/* Section: What can be checked */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">アクティビティで確認できる内容</h2>
        <p className="mb-4 text-gray-700">
          アクティビティでは以下のような操作履歴を確認できます。
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
          <li>請求書CSVアップロード</li>
          <li>顧客CSVアップロード</li>
          <li>顧客データ削除</li>
          <li>契約プラン変更</li>
          <li>Stripe APIキー設定</li>
          <li>税率IDの追加・変更・削除</li>
          <li>登録情報の変更</li>
          <li>お支払い方法（クレジットカード）の追加・削除</li>
          <li>デフォルトクレジットカードの変更</li>
          <li>メンバーの招待</li>
          <li>メンバー権限の変更</li>
        </ul>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section: How to check */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">アクティビティの確認方法</h2>
        <p className="mb-4 text-gray-700">
          左メニューの「アクティビティ」をクリックすると、操作履歴の一覧を確認できます。
        </p>
        <p className="mb-4 text-gray-700 font-semibold">
          一覧では以下の情報を確認できます。
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4 mb-4">
          <li>実行日時</li>
          <li>実行ユーザー</li>
          <li>操作内容</li>
          <li>対象データ</li>
          <li>実行結果</li>
        </ul>
        <p className="text-gray-700">
          対象の履歴をクリックすると詳細情報を確認できます。
        </p>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section: Detailed check */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">操作履歴の詳細確認</h2>
        <p className="mb-4 text-gray-700">
          各アクティビティでは、操作の詳細情報を確認できます。
        </p>
        <p className="mb-4 text-gray-700">
          例えば請求書CSVアップロードの場合は、対象のCSVファイル名や処理結果を確認できます。
        </p>
        <p className="text-gray-700">
          また、設定変更の場合は、どの設定が変更されたかを確認できます。
        </p>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section: CSV error check */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">CSVアップロードのエラー確認について</h2>
        <p className="mb-4 text-gray-700">
          アクティビティにはCSVアップロードの実行履歴とエラーの詳細を確認できます。
        </p>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section: Timing */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">アクティビティに記録されるタイミング</h2>
        <p className="text-gray-700">
          アクティビティは操作完了後に自動的に記録されます。<br />
          正常に完了した操作だけでなく、エラーにより失敗した操作も記録される場合があります。
        </p>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section: Security */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 font-bold">セキュリティについて</h2>
        <p className="mb-4 text-gray-700">
          アクティビティには操作内容のみが記録されます。<br />
          Stripe APIキーやクレジットカード番号などの機密情報は保存されません。
        </p>
        <p className="text-gray-700">
          安全性を考慮し、一部の情報はマスクされた状態で記録されます。
        </p>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Attention Section */}
      <section className="mb-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
          <p className="font-bold text-lg">注意</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li>アクティビティは監査目的の履歴です。</li>
            <li>記録されたアクティビティを編集または削除することはできません。</li>
            <li>また、アクティビティから各種操作を再実行することはできません。</li>
          </ul>
        </div>
      </section>

      <div className="mt-8 text-center">
        <Link 
          to="/app/support/how-to-use"
          className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-colors"
        >
          「使い方」一覧へ戻る
        </Link>
      </div>

    </div>
  );
};

export default ActivityGuide;
